import { getTimezoneOffset, owaDate } from 'owa-datetime';
import getTimeZoneNameInServerLanguage from './getTimeZoneNameInServerLanguage';

const UTC_OFFSET_REGEX: RegExp = new RegExp('UTC((\\+|-)\\d{2}:\\d{2}|.{0})');

/**
 * Build and returns the short display name for the provided timeZoneId.
 * E.g., UTC-8
 *
 * @param timeZoneId time zone to build short display name for
 */
export default function getTimeZoneShortDisplayName(timeZoneId: string): string {
    let fullUtcString = getFullUtcStringFromRegex(timeZoneId);

    if (!fullUtcString) {
        return buildShortUtcStringFromOffset(timeZoneId);
    }

    let shortDisplayName = tryRemoveMinutesTrailingZero(fullUtcString);
    shortDisplayName = tryRemoveHourLeadingZero(shortDisplayName);

    return shortDisplayName;
}

/**
 * Parses and returns the UTC offset string from full time zone display name for the provided time zone
 *
 * @param timeZoneId time zone to get the full UtcString
 */
function getFullUtcStringFromRegex(timeZoneId: string): string {
    let matches = getTimeZoneNameInServerLanguage(timeZoneId).match(UTC_OFFSET_REGEX);

    return matches && matches.length > 0 ? matches[0] : null;
}

/**
 * If possible, remove the trailing zeros from the UtcString.
 * if the UtcString ends in '00' the trailing zeros will be removed, otherwise they are not removed
 *
 * @param utcString
 */
function tryRemoveMinutesTrailingZero(utcString: string): string {
    let splitUtcString = utcString.split(':');
    if (splitUtcString && splitUtcString.length == 2 && splitUtcString[1] == '00') {
        return splitUtcString[0];
    }

    return utcString;
}

/**
 * If possible, remove the leading zero from the UtcString.
 * Does nothing if no leading zeros are found
 *
 * @param utcString
 */
function tryRemoveHourLeadingZero(utcString: string): string {
    const shortDisplayNameLength = utcString.length;

    if ((shortDisplayNameLength == 6 || shortDisplayNameLength == 9) && utcString[4] == '0') {
        return utcString.slice(0, 4) + utcString.slice(5);
    }

    return utcString;
}

/**
 * Builds the UTC string of the format UTC(+/-)H:D from the offset.
 * Since it's calculated from the offset for the timezone it will change
 * during daylight savings (Unlike the time zone full display name)
 *
 * @param timeZoneId time zone to build short display name for
 */
function buildShortUtcStringFromOffset(timeZoneId: string): string {
    const offset = getTimezoneOffset(owaDate(timeZoneId, undefined));
    const hour = Math.floor(Math.abs(offset / 60));
    const minutes = offset % 60;
    const sign = offset >= 0 ? '+' : '-';

    let displayName = 'UTC' + sign + hour;

    if (minutes) {
        displayName += ':' + minutes;
    }

    return displayName;
}
